import type { ComponentPropsWithoutRef } from 'react';
import { useState } from 'react';
import clsx from 'clsx';
import { Transition } from '@headlessui/react';

//hooks
import { ADD_MEDIA, REMOVE_MEDIA, useAlbumContext } from '@/utils/hooks/context/useAlbumContext';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

//pendo tracking
import { pendoTrack } from '@/components/tools/Pendo';
import { PENDO_EVENT_ALBUMS_ADD_MEDIA_TO_ALBUM_FROM_ALBUM } from '@/lib/tools/pendo/events';

//components
import AlbumPost from '@/components/pages/albums/AlbumFunnelModal/ModalSteps/ManualStep/components/post/index';
import Checkbox from '@/components/base/Checkbox';

type PostButtonProps = ComponentPropsWithoutRef<'div'> & { media: { id: string } };

export function PostButton({ media, ...restProps }: PostButtonProps) {
    const { albumInfo, dispatch } = useAlbumContext();
    const { activeSocialProfile } = useActiveSocialProfile();
    const [isHover, setIsHover] = useState(false);
    const isChecked = albumInfo?.medias?.includes(media?.id);

    return (
        <div className="z-0 rounded-md bg-white p-1 dark:bg-icoDarkMode-anthracite" {...restProps}>
            <div className="relative h-full w-full">
                <button
                    data-testid={`album-manual-post-button-${media.id}`}
                    onMouseEnter={() => setIsHover(true)}
                    onMouseLeave={() => setIsHover(false)}
                    onClick={() => {
                        dispatch({
                            type: isChecked ? REMOVE_MEDIA : ADD_MEDIA,
                            payload: media?.id
                        });
                        if (!isChecked) {
                            // Pendo : track event
                            pendoTrack(PENDO_EVENT_ALBUMS_ADD_MEDIA_TO_ALBUM_FROM_ALBUM);
                        }
                    }}
                    className="absolute inset-0 z-20 h-full w-full transition-all duration-150 hover:bg-icoBlack/40 focus:ring-0 focus:ring-offset-0 focus:ring-offset-transparent"
                    key={media?.id}>
                    <Transition
                        show={isChecked || isHover}
                        enter="transition-opacity duration-150"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity duration-150"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0">
                        <span
                            className={clsx(
                                'absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-m',
                                {
                                    'bg-icoBlack/10 backdrop-blur-20': !isChecked,
                                    'bg-icoBlue': isChecked
                                }
                            )}>
                            <Checkbox
                                checked={isChecked}
                                onChange={() => {
                                    //do nothing
                                }}
                                boxStyle="white"
                                boxSize="xs"
                                className="focus:ring-0"
                            />
                        </span>
                    </Transition>
                </button>
                <AlbumPost
                    shouldDisplayIcon={!isChecked && !isHover}
                    data={media}
                    network={activeSocialProfile?.social_network_type}
                    albumType={albumInfo?.criteria?.album_type}
                />
            </div>
        </div>
    );
}
